import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, of } from 'rxjs';
import { loadErrorMessage, toBankingError } from '@/app/common/banking/banking-api-errors';
import {
  deriveBankDetailsViewState,
  type BankDetailsViewState,
} from '@/app/common/banking/banking-state';
import type {
  AirwallexBeneficiaryDto,
  CreatorPaymentDetailDto,
} from '@/app/pages/admin-options/settings/molecules/creator-payment-details-fix/creator-payment-details.types';
import { AdminCreatorPaymentDetailsService } from '@/app/pages/admin-options/settings/services/admin-creator-payment-details.service';
import { CreatorPaymentDetailsForm } from './payment-details-form';

type FormMode = 'add' | 'edit' | null;

@Component({
  selector: 'ins-creator-payment-details',
  imports: [CreatorPaymentDetailsForm],
  templateUrl: './payment-details.html',
  host: { class: 'block h-full min-h-0 w-full min-w-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatorPaymentDetails implements OnInit {
  readonly viewState = signal<BankDetailsViewState>({ kind: 'loading' });
  readonly formMode = signal<FormMode>(null);
  readonly paymentDetail = signal<CreatorPaymentDetailDto | undefined>(undefined);

  readonly hasExisting = computed(() => !!this.paymentDetail()?.airwallexId);
  readonly availableBeneficiary = computed(() => {
    const state = this.viewState();
    return state.kind === 'available' ? state.beneficiary : undefined;
  });
  readonly errorState = computed(() => {
    const state = this.viewState();
    return state.kind === 'error' ? state : null;
  });
  readonly availableState = computed(() => {
    const state = this.viewState();
    return state.kind === 'available' ? state : null;
  });

  readonly creatorUserId = signal<string | undefined>(undefined);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly paymentDetailsService = inject(AdminCreatorPaymentDetailsService);

  ngOnInit(): void {
    const creatorId = this.route.parent?.snapshot.paramMap.get('creatorId') ?? undefined;
    this.creatorUserId.set(creatorId);
    if (creatorId) {
      this.loadPaymentDetails(creatorId);
    } else {
      this.viewState.set({
        kind: 'error',
        message: 'Creator ID is missing from the route.',
        canRetry: false,
      });
    }
  }

  loadPaymentDetails(creatorId?: string): void {
    const id = creatorId ?? this.creatorUserId();
    if (!id) return;

    this.viewState.set({ kind: 'loading' });
    this.formMode.set(null);

    this.paymentDetailsService
      .getBeneficiaryAdmin(id)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          const bankingErr = toBankingError(err);
          this.viewState.set({
            kind: 'error',
            message: loadErrorMessage(bankingErr.kind, bankingErr.status),
            canRetry: true,
          });
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (!response) return;
        this.paymentDetail.set(response);
        this.viewState.set(deriveBankDetailsViewState(response));
      });
  }

  openAddForm(): void {
    this.formMode.set('add');
  }

  openEditForm(): void {
    this.formMode.set('edit');
  }

  closeForm(): void {
    this.formMode.set(null);
  }

  onFormSaved(): void {
    this.formMode.set(null);
    const creatorId = this.creatorUserId();
    if (creatorId) {
      this.loadPaymentDetails(creatorId);
    }
  }

  formatTransferMethods(methods: string[] | undefined): string {
    return methods?.length ? methods.join(', ') : '—';
  }

  displayValue(value: string | undefined | null): string {
    return value?.trim() ? value : '—';
  }

  obfuscateSensitiveValue(value: string | undefined | null, visibleSuffix = 4): string {
    const trimmed = value?.trim();
    if (!trimmed) {
      return '—';
    }
    if (trimmed.length <= visibleSuffix) {
      return '*'.repeat(trimmed.length);
    }
    return `${'*'.repeat(Math.max(4, trimmed.length - visibleSuffix))}${trimmed.slice(-visibleSuffix)}`;
  }

  beneficiaryForForm(): AirwallexBeneficiaryDto | undefined {
    return this.availableBeneficiary();
  }
}
